/* eslint-disable @typescript-eslint/no-misused-promises */
import dayjs from 'dayjs';
import { cloneDeep, get, set } from 'lodash';
// modules
import LiveDataService from 'modules/LiveData';
import SearchService from 'modules/Search';
import Validation from 'modules/Validation';
import FormatService from 'modules/Format';
// typings
import { MarketFeederV2 } from 'modules/LiveData/typings';
// consts
import { ExtendedFeedDetails, MarketFullFeed, OHLC } from 'app/marketDataFeed';
import { MARKET_FEEDER_MODES } from 'modules/LiveData/constants';
import {
  decomposeInstrumentKey,
  decomposeSegment,
} from 'modules/BOD/modules/Search/utils';
import { CUSTOM_EXCHANGE_TOKEN_SEPARATOR } from 'modules/Watchlists/constants';
// typings
import { DrawingSources } from 'modules/ChartSync/typings';
import UnderlyingCharts from 'modules/UnderlyingCharts';
import { EntityId, LineToolState } from './components/TradingView/sources/charting_library.min';
// consts
import { INDICATOR_INPUT_KEY_MAP, ServerIntervals } from './consts';
// store
import ChartsStore from './store';

class TradingChartService extends ChartsStore {
  public subscribers: ChartLiveBarSubscribers = {};
  private previousMinuteData: Record<string, PreviousIntervalData> = {};

  private getIntervalOHLC(resolution: string) {
    if (Number.isInteger(+resolution)) {
      return 'I1';
    }

    if (resolution === '1D') {
      return ServerIntervals.ONE_DAY;
    }

    return resolution;
  }

  public getMappedIntervalValue(resolution: string) {
    if (Number.isInteger(+resolution)) {
      return ServerIntervals.ONE_MINUTE;
    }

    if (resolution === '1D') {
      return ServerIntervals.ONE_DAY;
    }

    return resolution;
  }

  public isSubscribedToToken(token: string): boolean {
    let isSubscribed = false;

    Object.values(this.subscribers).forEach(subscriber => {
      if (subscriber.ticker === token) {
        isSubscribed = true;
      }
    });

    return isSubscribed;
  }

  public subscribe(subscriptionOptions: ChartLiveBarSubscriber) {
    if (!this.subscribers[subscriptionOptions.guid]) {
      LiveDataService.subscribeScrip(
        [subscriptionOptions?.instrumentKey],
        MARKET_FEEDER_MODES.FULL,
        subscriptionOptions.guid,
        true,
      );
    } else {
      const currentSubscriber = this.subscribers[subscriptionOptions.guid];
      currentSubscriber.listeners = {
        ...currentSubscriber.listeners,
        ...subscriptionOptions.listeners,
      };
      return;
    }

    this.subscribers[subscriptionOptions.guid] = subscriptionOptions;
  }

  public resetCharts() {
    if (this.subscribers) {
      Object.values(this.subscribers).forEach(subscriptionOptions =>
        subscriptionOptions.onResetCacheNeededCallback(),
      );
    }
  }

  public resetActiveChart(subscriberToken: string, interval: string) {
    if (this.subscribers) {
      const activeSubscriber = Object.entries(this.subscribers)
        .filter(([_, subscriber]) => {
          const mappedInterval = this.getMappedIntervalValue(
            subscriber.interval,
          );
          return (
            mappedInterval === interval && subscriber.ticker === subscriberToken
          );
        })
        .map(([subscriberTokenKey]) => subscriberTokenKey);

      if (activeSubscriber.length) {
        activeSubscriber.forEach(subscriber => {
          this.subscribers[subscriber].onResetCacheNeededCallback();
        });
      }
    }
  }

  public ohlcStringToBar(
    barData: number[],
    isIndex = false,
    customOIIndicator = false,
  ): Bar {
    const [time, open, high, low, close, volume, openInterest] = barData;

    const result: Bar = customOIIndicator ? {
      time,
      close: openInterest,
      open: openInterest,
      high: openInterest,
      low: openInterest,
    } : {
      time,
      close,
      open,
      high,
      low,
    };

    if (!isIndex) {
      result.volume = volume;
    }

    return result;
  }

  public ohlcToBar(
    ohlcData: OHLC,
    isIndex = false,
    customOIIndicator = false,
    extendedFeedDetails?: ExtendedFeedDetails,
  ): Bar {
    const oi = extendedFeedDetails?.oi || 0;
    const result: Bar = customOIIndicator ? {
      time: ohlcData.ts,
      close: oi,
      open: oi,
      high: oi,
      low: oi,
    } : {
      time: ohlcData.ts,
      close: ohlcData.close,
      open: ohlcData.open,
      high: ohlcData.high,
      low: ohlcData.low,
    };

    if (!isIndex) {
      result.volume = ohlcData.vol;
    }

    return result;
  }

  private findCurrentIntervals = (
    ohlcData: OHLC[],
    subscriberInterval: string,
  ) => ohlcData.filter(ohlc => ohlc.interval === subscriberInterval);

  private shouldUpdateBar = (
    currentTs: number,
    bar: Bar,
  ): boolean => {
    const startTimeForCurrentTsMinute = dayjs(currentTs).tz().startOf('minute');

    // If bar from socket is before current timestamp minute, return false
    if (dayjs(bar.time).tz().isBefore(startTimeForCurrentTsMinute)) {
      return false;
    }

    return true;
  };

  private checkIfNextMinuteStarted = (
    bar: Bar,
    subscriberToken: string,
  ): boolean => {
    const previousBarTime = this.previousMinuteData[subscriberToken]
      ?.previousBarTime;
    const isNextMinuteStarted = !dayjs(previousBarTime)
      .tz()
      .isSame(dayjs(bar.time).tz());

    return isNextMinuteStarted;
  };

  public formCandleData = (
    currentTs: number,
    subscriber: ChartLiveBarSubscriber,
    subscriberToken: string,
    candle: Bar,
    previousBar: Bar,
  ) => {
    const { interval } = subscriber;
    const bar = cloneDeep(candle);
    // For 1D interval, directly update bar data from web socket
    if (interval === '1D') {
      console.log('form candle data 1D ::', subscriberToken, bar);
      subscriber.onDataReceived(bar);
      return;
    }

    // If bar from socket is before current timestamp minute, return because it can be old data
    // or the ohlc feed is not updated yet for the bar time
    if (!this.shouldUpdateBar(currentTs, bar)) {
      console.log('bar from socket is before current timestamp minute,', subscriberToken);
      return;
    }

    // Check if first bar from feed, so previousMinuteData is not present
    const isFirstBarFromFeed = !this.previousMinuteData[subscriberToken];

    if (isFirstBarFromFeed) {
      console.log('form candle data - first bar from feed ::', subscriberToken, dayjs(bar.time).tz().format('HH:mm:ss'));
      this.previousMinuteData[subscriberToken] = {
        ...this.previousMinuteData[subscriberToken],
        previousBarTime: bar.time,
      };
    }

    // Check if next minute started by comparing previous bar time and current bar time
    const isNextMinuteStarted = this.checkIfNextMinuteStarted(
      bar,
      subscriberToken,
    );

    if (isNextMinuteStarted) {
      this.previousMinuteData[subscriberToken] = {
        ...this.previousMinuteData[subscriberToken],
        shouldUpdatePrevMinuteData: true,
      };
    }

    if (this.previousMinuteData[subscriberToken]?.shouldUpdatePrevMinuteData) {
      console.log('form candle data - update prev bar data ::', subscriberToken, isNextMinuteStarted, previousBar.volume, dayjs(previousBar.time).tz().format('HH:mm:ss'));
      subscriber.onDataReceived(previousBar);

      this.previousMinuteData[subscriberToken] = {
        ...this.previousMinuteData[subscriberToken],
        shouldUpdatePrevMinuteData: false,
        previousBarTime: bar.time,
      };
    }
    console.log('form candle data - update current bar data ::', subscriberToken, bar.volume, dayjs(bar.time).tz().format('HH:mm:ss'));
    subscriber.onDataReceived(bar);
  };

  public handleGetOHLCDataV2: WsHandler<
    MarketFeederV2.LiveDataDetails
  > = message => {
    Object.entries(message.data.feeds).forEach(
      async ([instrumentKey, data]) => {
        const { segment } = decomposeInstrumentKey(instrumentKey);

        const scripData = await SearchService.searchByInstrumentKey(
          instrumentKey,
        );

        const { currentTs } = message.data;

        let currentSubscribers = Object.entries(this.subscribers)
        .filter(([, subscriber]) =>
          [ServerIntervals.ONE_MINUTE, ServerIntervals.ONE_DAY].some(
            (interval: ServerIntervals) => {
              const mappedInterval = this.getMappedIntervalValue(
                subscriber.interval,
              );
              return (
                mappedInterval === interval &&
                subscriber.ticker === scripData?.exchangeToken
              );
            },
          ),
        )
        .map(([subscriberToken]) => subscriberToken);

        // Store main symbol intervals in a Set for quick lookup
        const mainIntervals = new Set();

        currentSubscribers.forEach(sub => {
          if (!sub.includes('#customOI')) {
            const interval = sub.split('_#_').pop();
            mainIntervals.add(interval);
          }
        });

        // Filter out customOI symbols that don't have a corresponding main symbol
        currentSubscribers = currentSubscribers.filter(sub => {
          if (!sub.includes('#customOI')) return true; // Keep main symbols

          const interval = sub.split('_#_').pop();
          return mainIntervals.has(interval); // Retain if a main symbol exists
        });

        currentSubscribers.forEach(subscriberToken => {
          const subscriber = this.subscribers[subscriberToken];
          const customOIIndicator = subscriberToken.includes('#customOI');

          if (subscriber) {
            const { series } = decomposeSegment(segment);
            const isIndex = Validation.isIndex(series);
            const subscriberInterval = this.getIntervalOHLC(
              subscriber.interval,
            );

            const ohlc =
              data?.ff?.[isIndex ? 'indexFF' : 'marketFF']?.marketOHLC?.ohlc;
            const extendedFeedDetails =
              (data?.ff?.[isIndex ? 'indexFF' : 'marketFF'] as MarketFullFeed)?.eFeedDetails;
            if (!ohlc) {
              return;
            }

            const currentIntervals = this.findCurrentIntervals(
              ohlc,
              subscriberInterval,
            );

            const currentMinuteInterval =
              currentIntervals[currentIntervals.length - 1];

            const previousMinuteInterval = currentIntervals[0];
            if (currentMinuteInterval) {
              const bar = this.ohlcToBar(
                currentMinuteInterval,
                isIndex,
                customOIIndicator,
                extendedFeedDetails,
              );
              const previousMinuteBar = this.ohlcToBar(
                previousMinuteInterval,
                isIndex,
                customOIIndicator,
                extendedFeedDetails,
              );
              this.formCandleData(
                currentTs,
                subscriber,
                subscriberToken,
                bar,
                previousMinuteBar,
              );
            }
          }
        });
      },
    );
  };

  public unsubscribe(guid: string, listenerGuid: string): void {
    delete this.subscribers[guid].listeners[listenerGuid];
    delete this.previousMinuteData[guid];

    // NOTE: If there are any listeners, continue receiving data
    if (Object.values(this.subscribers[guid].listeners).length) {
      return;
    }

    const { instrumentKey, ticker } = this.subscribers[guid];

    const needToUnsubscribeScrip =
      Object.keys(this.subscribers).filter(subscriber =>
        subscriber.includes(ticker),
      ).length <= 1;

    if (needToUnsubscribeScrip) {
      LiveDataService.unsubscribeScrip(
        [instrumentKey],
        MARKET_FEEDER_MODES.FULL,
        guid,
        true,
      );
    }

    delete this.subscribers[guid];
    this.clearExistingChartUpdateIntervals(guid);
  }

  public setChartActiveSymbolData = async (symbol: string) => {
    const scripData =
      (await SearchService.searchByExchangeToken(symbol)) ?? ({} as DataSchema);
    this.setActiveSymbolData(scripData);
  };

  public getScripDataFromTVExchangeToken = async (symbol: string) => {
    const exchangeToken = FormatService.decodeCustomTvExchangeToken(symbol);
    const scripData = await SearchService.searchByExchangeToken(exchangeToken);
    return scripData;
  };

  public convertTVTokenToInstrumentKey = async (tvExchangeToken: string) => {
    const exchangeToken = FormatService.decodeCustomTvExchangeToken(
      tvExchangeToken || '',
    );
    const scripData = await SearchService.searchByExchangeToken(exchangeToken);
    return scripData?.instrumentKey;
  };

  public convertInstrumentKeyToTVToken = async (instrumentKey: string) => {
    const scripData = await SearchService.searchByInstrumentKey(instrumentKey);
    const exchangeToken = scripData?.exchangeToken || '';
    const tvExchangeToken = FormatService.generateCustomTvExchangeToken(
      scripData?.displayName ?? '',
      exchangeToken,
    );

    return tvExchangeToken;
  };

  public getDrawingKey = (
    layoutId: string | undefined,
    chartId: string | number,
  ) => `${layoutId}/${chartId}`;

  public updateDrawingsSymbolWithInstrumentKey = async (
    chartsData: ContentChartsType,
  ) => {
    const charts = cloneDeep(chartsData);
    // Converting symbol in drawings from tvExchangeToken to instrumentKey when saving to server
    await Promise.all(
      charts.map(async chart => {
        const panes = get(chart, 'panes', []);
        if (panes?.length > 0) {
          await this.updatePaneSymbols(panes);
        }
      }),
    );

    return charts;
  };

  public async updatePaneSymbols(panes: ContentPanes) {
    await Promise.all(
      panes.map(async pane => {
        const sources = get(pane, 'sources');
        if (sources) {
          await this.updateSourceSymbols(sources);
        }
      }),
    );
  }

  private async updateSourceSymbols(sources: ContentSources) {
    await Promise.all(
      sources.map(async source => {
        // update sourceState.inputs for specific study
        // i.e. Ratio, Spread, Coefficient
        await this.updateSourceStateInputsWithInstrumentKey(source);
        const sourceState = get(source, 'state');
        const symbol = get(sourceState, 'symbol');

        if (symbol) {
          const instrumentKey = await this.convertTVTokenToInstrumentKey(
            symbol,
          );
          if (instrumentKey) {
            set(sourceState, 'symbol', instrumentKey);
          }
        }
      }),
    );
  }

  private async updateSourceStateInputsWithInstrumentKey(
    source: ContentSource,
  ) {
    const indicatorName: string = get(source, 'metaInfo.description', '');
    const inputKeys = INDICATOR_INPUT_KEY_MAP[indicatorName];
    // If indicator name is not present in the map, return
    if (!inputKeys) return;

    const sourceStateInputs = get(source, 'state.inputs');
    // If sourceStateInputs is not present, return
    if (!sourceStateInputs) return;
    await Promise.all(
      inputKeys.map(async key => {
        const inputValue = get(source, `state.inputs.${key}`, '');
        if (inputValue) {
          const instrumentKey = await this.convertTVTokenToInstrumentKey(
            inputValue,
          );
          if (instrumentKey) {
            set(source, `state.inputs.${key}`, instrumentKey);
          }
        }
      }),
    );
  }

  public updateChartDataWithTVToken = async (chartsData: ContentChartsType) => {
    const charts = cloneDeep(chartsData);
    if (charts) {
      await Promise.all(
        charts.map(async chart => {
          const panes = get(chart, 'panes', []);
          if (panes?.length > 0) {
          // remove panes with no sources
          const filteredPanes = panes.filter((pane) => {
            const sources = get(pane, 'sources', []);
            return sources.length > 0;
          });
          set(chart, 'panes', filteredPanes);
          await this.updatePaneSources(filteredPanes);
          }
        }),
      );
    }

    return charts;
  };

  public async updatePaneSources(panes: ContentPanes) {
    await Promise.all(
      panes.map(async pane => {
        const sources = get(pane, 'sources');
        if (sources) {
          const updatedSources = await this.getUpdatedSources(sources);
          const filteredSources = this.filterValidSources(updatedSources);
          set(pane, 'sources', filteredSources);
        }
      }),
    );
  }

  async getUpdatedSources(
    sources: ContentSources,
  ): Promise<{ source: ContentSource; isValid: boolean }[]> {
    return Promise.all(
      sources.map(async (source: ContentSource) => {
        // update sourceState.inputs for specific study
        // i.e. Ratio, Spread, Coefficient
        await this.updateSourceStateInputsWithTVToken(source);

        const sourceState = get(source, 'state') || {};
        const symbol = get(sourceState, 'symbol') ?? '';

        if (!symbol) {
          return { source, isValid: true };
        }

        const isTVExchangeToken = symbol?.includes(
          CUSTOM_EXCHANGE_TOKEN_SEPARATOR,
        );
        const scripData = await this.getScripData(symbol, isTVExchangeToken);

        if (scripData && !isTVExchangeToken) {
          const tvExchangeToken = await this.convertInstrumentKeyToTVToken(
            symbol,
          );
          if (tvExchangeToken) {
            set(sourceState, 'symbol', tvExchangeToken);
          }
        }

        return { source, isValid: !!scripData };
      }),
    );
  }

  private async updateSourceStateInputsWithTVToken(source: ContentSource) {
    const indicatorName: string = get(source, 'metaInfo.description', '');
    const inputKeys = INDICATOR_INPUT_KEY_MAP[indicatorName];
    // If indicator name is not present in the map, return
    if (!inputKeys) return;

    const sourceStateInputs = get(source, 'state.inputs');
    // If sourceStateInputs is not present, return
    if (!sourceStateInputs) return;
    await Promise.all(
      inputKeys.map(async key => {
        const inputValue = get(source, `state.inputs.${key}`, '');
        if (inputValue) {
          const tvExchangeToken = await this.convertInstrumentKeyToTVToken(
            inputValue,
          );
          if (tvExchangeToken) {
            set(source, `state.inputs.${key}`, tvExchangeToken);
          }
        }
      }),
    );
  }

  filterValidSources(
    updatedSources: { source: ContentSource; isValid: boolean }[],
  ) {
    return updatedSources
      .filter(({ isValid, source }) => {
        const type = get(source, 'type');
        const sourceState = get(source, 'state') || {};
        const symbol = get(sourceState, 'symbol') ?? '';
        // Only MainSeries and valid scrips which are not expired are saved
        return type === 'MainSeries' ? !!symbol : isValid;
      })
      .map(({ source }) => source);
  }

  public async getScripData(symbol: string, isTVExchangeToken: boolean) {
    if (isTVExchangeToken) {
      return this.getScripDataFromTVExchangeToken(symbol);
    }
    return SearchService.searchByInstrumentKey(symbol);
  }

  public updateChartLayoutWithInstrumentKey = async (
    chartsData: ContentChartsType,
  ) => {
    const charts = cloneDeep(chartsData);
    const drawingsSources: DrawingSources = {};

    await Promise.all(charts.map(
      async (chart) => this.processChart(chart)));

    return { charts, drawingsSources };
  };

  private processChart = async (
    chart: ContentChartType,
  ) => {
    const panes = get(chart, 'panes', []);
    if (panes?.length > 0) {
      // remove panes with no sources
      const filteredPanes = panes.filter((pane) => {
        const sources = get(pane, 'sources', []);
        return sources.length > 0;
      });
      set(chart, 'panes', filteredPanes);
      await this.processPanes(filteredPanes);
    }
  };

  private processPanes = async (
    panes: ContentPanes,
  ) => {
    await Promise.all(panes.map(async (pane) => {
      const sources = get(pane, 'sources');
      if (sources) {
        const filteredSources = await this.processSources(sources);
        set(pane, 'sources', filteredSources);
      }
    }));
  };

  private isSourceUnderlyingChart(source: ContentSource) {
    const isUnderlyingChart = UnderlyingCharts.underlyingChartsList.find(
      uc => uc.id === source.id,
    );
    return !!isUnderlyingChart;
  }

  private processSources = async (
    sources: ContentSources,
  ) => {
    const filteredSources: ContentSources = [];

    await Promise.all(
      sources.map(async source => {
        const sourceState = get(source, 'state', {}) || {};
        const symbol = get(sourceState, 'symbol', '');
        const isUnderlyingChart = this.isSourceUnderlyingChart(source);
        if (!symbol && !isUnderlyingChart) {
          // update sourceState.inputs for specific study
          // i.e. Ratio, Spread, Coefficient
          await this.updateSourceStateInputsWithInstrumentKey(source);
          // No symbol: directly push to filtered sources
          filteredSources.push(source);
          return;
        }

        const instrumentKey = await this.convertTVTokenToInstrumentKey(symbol);

        if (instrumentKey) {
          set(sourceState, 'symbol', instrumentKey);
        }

        // Update source state symbol with instrumentKey only if its present
        filteredSources.push(source);
      }),
    );

    return filteredSources;
  };

  private processLocalSources(
    sources: ContentSources,
  ) {
    return sources.filter((source) => {
      // filtering out underlying charts' sources.
      const isUnderlyingChart = this.isSourceUnderlyingChart(source);
      return !isUnderlyingChart;
    });
  }

  private processLocalPanes(panes: ContentPanes) {
    panes.forEach((pane) => {
      const sources = get(pane, 'sources');
      if (sources) {
        const filteredSources = this.processLocalSources(sources);
        set(pane, 'sources', filteredSources);
      }
    });
  }

  private processLocalChart(chart: ContentChartType) {
    const panes = get(chart, 'panes', []);
    if (panes?.length > 0) {
      this.processLocalPanes(panes);
    }
    return chart;
  }

  public getFilteredLocalChartState(chartsData: ContentChartsType) {
    const charts = cloneDeep(chartsData);
    charts.forEach((chart) => this.processLocalChart(chart));
    return charts;
  }

  async updateDrawingsSourcesWithTVToken(
    rawSources: Record<string, LineToolState | null>,
  ): Promise<Map<EntityId, LineToolState | null>> {
    const clonedSources = cloneDeep(rawSources);
    const updatedSources = new Map<EntityId, LineToolState | null>();

    const updatedSourcesWithTVToken = Object.entries(clonedSources).map(
      async ([entityId, lineToolState]) => {
        if (lineToolState) {
          const instrumentKey = get(lineToolState, 'symbol', '');
          const isTVExchangeToken = instrumentKey.includes(
            CUSTOM_EXCHANGE_TOKEN_SEPARATOR,
          );
          const scripData = await this.getScripData(
            instrumentKey,
            isTVExchangeToken,
          );

          // convert instrumentKey to tvExchangeToken  only if scripData is available
          // i.e. scrip is not expired
          if (scripData && !isTVExchangeToken) {
            const tvExchangeToken = await this.convertInstrumentKeyToTVToken(
              instrumentKey,
            );
            if (tvExchangeToken) {
              // update the source with TV token
              set(lineToolState, 'symbol', tvExchangeToken);
              set(lineToolState, 'state.state.symbol', tvExchangeToken);
            }
            updatedSources.set(entityId as EntityId, lineToolState);
          }
        }
      },
    );

    await Promise.all(updatedSourcesWithTVToken);
    return updatedSources;
  }

  getLineToolsAndGroupsSources(
    content: string,
  ): Record<string, LineToolState | null> {
    let rawSources: Record<string, LineToolState | null> = {};

    const contentData = JSON.parse(content || '{}');
    rawSources = get(contentData, 'sources', null);

    return rawSources;
  }

  getMainSeriesPane = (panes: ContentPanes) => (
    panes.find(pane => {
      const sources = get(pane, 'sources', []);
      return this.getMainSeriesSource(sources) !== null;
    }) || null
  );

  getMainSeriesSource = (sources: ContentSources) =>
    sources.find(source => get(source, 'type') === 'MainSeries') || null;
}
export default new TradingChartService();
